"use client";

import { Core } from "@faire/design-tokens";
import { formatBytes } from "@faire/web/common/formatBytes";
import { useStrictLocalization } from "@faire/web/common/localization";
import { LoadingSpinner } from "@faire/web/slate-deprecated/LoadingSpinner";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";

import { Upload } from "../../slate/icons/foundation/Upload";
import { Spacer } from "../../slate/spacing";

interface IProps {
  uploadCta?: string;
  displayedFileTypes?: string;
  displayedMaxFileSize?: number;
  isLoading?: boolean;
  error?: boolean;
}

export const ImageUploaderStatus: React.FC<IProps> = ({
  uploadCta,
  displayedFileTypes,
  displayedMaxFileSize,
  isLoading = false,
  error = false,
}) => {
  const { strictLocalize } = useStrictLocalization();
  if (isLoading) {
    return (
      <>
        <LoadingSpinner color={Core.icon.default} />
        {uploadCta ? (
          <>
            <Spacer size="1x" />
            <Typography color="inherit" align="center">
              <StrictLocalMsg
                id="2k6So7"
                defaultMessage="Uploading..."
                description={{
                  text: "Indicates that a file is currently uploading",
                }}
              />
            </Typography>
          </>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Upload color="inherit" fontSize="24px" />
      {uploadCta ? (
        <>
          <Spacer size="1x" />
          <Typography
            color={error ? Core.text.critical : "inherit"}
            align="center"
          >
            {uploadCta}
          </Typography>
          {displayedFileTypes || displayedMaxFileSize ? (
            <Spacer size="1x"></Spacer>
          ) : null}
          {displayedFileTypes ? (
            <Typography
              color={error ? Core.text.critical : "inherit"}
              align="center"
              variant="labelSansRegular"
            >
              <StrictLocalMsg
                id="d4dQxN"
                defaultMessage="Accepts: {displayedFileTypesFullyTranslatedSentence}"
                description={{
                  text: "A message telling the user what types of files the component accepts.",
                }}
                values={{
                  displayedFileTypesFullyTranslatedSentence: displayedFileTypes,
                }}
              />
            </Typography>
          ) : null}
          {displayedMaxFileSize ? (
            <Typography
              color={error ? Core.text.critical : "inherit"}
              align="center"
              variant="labelSansRegular"
            >
              <StrictLocalMsg
                id="wAOWyq"
                defaultMessage="Maximum file size is {formatBytesNumberWithUnit}"
                description={{
                  text: "A message telling the user the maximum size of image they can upload.",
                }}
                values={{
                  formatBytesNumberWithUnit: `${formatBytes(
                    displayedMaxFileSize,
                    strictLocalize
                  )}`,
                }}
              />
            </Typography>
          ) : null}
        </>
      ) : null}
    </>
  );
};
